import { computed, inject, Injectable, InjectionToken, Signal } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router'
import { _first, _last } from '@naturalcycles/js-lib'
import {
  clearblueFlowDefinition,
  FlowDefinitionName,
  signupFlowDefinition,
} from '@src/app/constants/flows.js'
import { AnyPage } from '@src/app/shared/typings/enum/pages.js'
import { SignalState, SignalStore } from '../store/signalStore.js'
import { logger } from '../util/log.util.js'
import { PagePath } from '../util/pagePath.js'

export type Path = AnyPage | PagePath

export interface PageFlowFnArgs {
  state: SignalState
  params: Params
}

export interface PageFlowMember {
  /**
   * Evaluates to true when this page is considered complete.
   */
  isComplete: (args: PageFlowFnArgs) => boolean
}

export type PageFlowCondition = (args: PageFlowFnArgs) => Path[]

export interface PageFlowDefinition {
  /**
   * The name of this flow definition.
   */
  name: FlowDefinitionName
  /**
   * The members of the flow. Pages not in this map are not part of the flow.
   * The key should be a page or the result of a PagePath.toString().
   *
   * @example
   * {
   *   [Page.PageA]: {
   *     // PageA is only considered complete when someCondition is true
   *     isComplete: (state) => state.someCondition
   *   },
   * )
   */
  members: Record<string, PageFlowMember>
  /**
   * A map of conditions that can modify the flow from (and including) a page.
   *
   * @example
   * {
   *   // Only go to PageB with PageC as the next page if someCondition is true, otherwise go straight to PageD
   *   [Page.PageB]: (state) => {
   *     if (state.someCondition) return [Page.PageB, Page.PageC]
   *     return [Page.D]
   *   }
   *   ...
   * }
   *
   */
  conditions: Record<string, PageFlowCondition>
  /**
   * This should be defined as the flow up until, and including, the first condition.
   * In the simplest case, this is the whole flow. Unfortunately, this is not a simple world.
   * Most likely it will be something like [page1, page2] where page2 has a condition that determines
   * whether to go to continue with [page3, page4] or [page5, page6].
   */
  initialFlow: Path[]
}

@Injectable({ providedIn: 'root' })
export abstract class AbstractPageFlowService {
  private store: SignalStore = inject(SignalStore)

  abstract flowDefinitionName: FlowDefinitionName

  abstract _flowDefinition: PageFlowDefinition

  private flow: Signal<Path[]> = computed(() => {
    const persistedFlow = this.store.$flow()[this.flowDefinition.name]
    if (persistedFlow.length) return persistedFlow
    return this.flowDefinition.initialFlow
  })

  public get flowDefinition(): PageFlowDefinition {
    return this._flowDefinition
  }

  public set flowDefinition(value: PageFlowDefinition) {
    this._flowDefinition = value
    this.store.$flow.set({ ...this.store.$flow(), [this.flowDefinitionName]: value.initialFlow })
  }

  /**
   * Helper that resolves the canActivate status for the given page.
   */
  private resolveCanActivate(path: Path, params: Params): true | Path {
    // If the page is not part of the flow definition, we guide the user back to the flow
    if (!this.flowDefinition.members[path.toString()]) {
      return _last(this.flow())
    }

    this.processCondition(path, params)

    // Now that we've processed conditions, we can check if the page can be activated
    const flowIndex = this.flow().findIndex(p => p.toString() === path.toString())
    if (flowIndex === -1) {
      return _last(this.flow())
    }

    // If the page is the first page, we can always activate it or we messed up big time
    if (flowIndex === 0) return true

    const previousPage = this.flow()[flowIndex - 1]! // We can non-null assert because flowIndex > 0
    const prevMember = this.flowDefinition.members[previousPage.toString()]

    // If the previous page is not part of the flow, we guide the user back to the flow
    if (!prevMember) {
      return _last(this.flow())
    }

    // Otherwise, if the previous page is complete, we can activate this page
    return prevMember.isComplete({ state: this.store.getState(), params }) || previousPage
  }

  /**
   * Returns true if the page can be activated, or a string or string[] to redirect to.
   * This function will follow a chain of canActivate checks until it reaches a page that can be activated.
   * This helps us skip pages without sending the user on a redirect chain.
   */
  canActivate(path: Path, params: Params): true | Path {
    // We make sure the flow is up to date
    this.processConditions(params)

    // Then we resolve the canActivate status for the given page
    let canActivate = this.resolveCanActivate(path, params)
    if (canActivate === true) return true
    // If we get a string or string[], we start resolving the canActivate status for that page
    // and repeat the process until we can activate a page
    while (canActivate !== true) {
      path = canActivate
      canActivate = this.resolveCanActivate(path, params)
    }
    return path
  }

  getFirstPage(): Path {
    return _first(this.flow())
  }

  getLastPage(): Path {
    return _last(this.flow())
  }

  /**
   * Processes conditions for the given page and updates the flow accordingly.
   */
  private processCondition(page: Path, params: Params): void {
    const index = this.flow().findIndex(p => p.toString() === page.toString())

    if (index === -1) return // If the page is not in the flow, we can't process anything

    let newPath: Path | null = null

    // We keep processing conditions until the page is stable
    while (newPath !== page && page) {
      newPath = page
      const condition = this.flowDefinition.conditions[page.toString()]
      if (condition) {
        const conditionResult = condition({ state: this.store.getState(), params })

        /**
         * A condition for Page B that returns this:
         * [Page A, Page B, Page C]
         * risks creating a loop of:
         * [Page A, <Page B>, Page C] -> [Page A, <Page A, <Page B>, Page C>, Page C] -> [Page A, <Page A, <Page A, Page B, Page C>, Page C>, Page C] -> ...
         */
        if (conditionResult.slice(1).some(p => p.toString() === page.toString())) {
          throw new Error(
            `Condition for ${page} includes the page itself at index > 0. This will create an infinite loop.`,
          )
        }

        const currentFlow = this.store.$flow()
        const currentFlowForDefinition = currentFlow[this.flowDefinitionName] || []
        this.store.$flow.set({
          ...currentFlow,
          [this.flowDefinitionName]: [
            ...currentFlowForDefinition.slice(0, index),
            ...conditionResult,
          ],
        })
        page = this.flow()[index] || page
      }
    }
  }

  /**
   * Processes conditions for the whole flow.
   * It's cheap and it saves us from some headaches.
   */
  processConditions(params: Params): void {
    let i = 0
    while (i < this.flow().length) {
      const member = this.flow()[i]
      if (!member) return
      this.processCondition(member, params)
      i++
    }
  }

  /**
   * Returns the next page in the flow.
   */
  getNextPage(path: Path, params: Params): Path {
    const index = this.flow().findIndex(p => p.toString() === path.toString())
    let landingPage: Path
    if (index === -1) {
      landingPage = _last(this.flow())
    } else {
      landingPage = this.flow()[index + 1] || path
    }
    const canActivate = this.canActivate(landingPage, params)
    if (canActivate === true) return landingPage
    return canActivate
  }

  /**
   * Returns the previous page in the flow.
   */
  getPreviousPage(path: Path): Path {
    const index = this.flow().findIndex(p => p.toString() === path.toString())
    if (index === -1) return _last(this.flow())
    // If the page is the first page, we return the page itself since there is no previous page
    if (index === 0) return path
    return this.flow()[index - 1] || path
  }
}

@Injectable({ providedIn: 'root' })
export class SignupFlowService extends AbstractPageFlowService {
  flowDefinitionName = FlowDefinitionName.SIGNUP
  override _flowDefinition: PageFlowDefinition = signupFlowDefinition

  constructor() {
    super()
    this.flowDefinition = this._flowDefinition
  }
}

@Injectable({ providedIn: 'root' })
export class ClearblueFlowService extends AbstractPageFlowService {
  flowDefinitionName = FlowDefinitionName.CLEAR_BLUE
  override _flowDefinition: PageFlowDefinition = clearblueFlowDefinition

  constructor() {
    super()
    this.flowDefinition = this._flowDefinition
  }
}

/* eslint-disable @typescript-eslint/naming-convention */
export const PageFlowService = new InjectionToken<AbstractPageFlowService>('PageFlowService')
/* eslint-enable @typescript-eslint/naming-convention */
/**
 * Returns a flowService-compliant canActivate function for the given page.
 */
export function getFlowGuard(path: Path): CanActivateFn {
  return (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    logger.log('Activating', typeof path === 'string' ? path : path.toString())
    const router = inject(Router)
    const store = inject(SignalStore)
    const flowService = inject(PageFlowService)

    const { queryParams } = state.root
    const flowCanActivate = flowService.canActivate(path, queryParams)
    if (flowCanActivate === true) return true
    const lang = store.$lang()
    const flowCanActivatePathFragments =
      typeof flowCanActivate === 'string' ? [flowCanActivate] : flowCanActivate.pages
    const urlTree = router.createUrlTree([lang, ...flowCanActivatePathFragments], {
      queryParams,
    })
    return urlTree
  }
}
